class WdReturnRequestForm extends HTMLElement {
  ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  connectedCallback() {
    this.form = this.querySelector('[data-wrf-form]');
    this.submitButton = this.querySelector('[data-wrf-submit]');
    this.defaultSubmitText = this.submitButton.textContent;
    this.statusEl = this.querySelector('[data-wrf-status]');
    this.strings = JSON.parse(this.querySelector('[data-wrf-strings]').textContent);
    this.maxImages = Number(this.dataset.maxImages || '5');
    this.maxImageSizeMb = Number(this.dataset.maxImageSizeMb || '5');
    this.scriptUrl = this.dataset.scriptUrl;
    this.files = [];

    this.dropzone = this.querySelector('[data-wrf-dropzone]');
    this.fileInput = this.querySelector('[data-wrf-file-input]');
    this.previewList = this.querySelector('[data-wrf-previews]');

    this.reasonSelect = this.form.querySelector('[data-wrf-field="reasonForReturn"]');
    this.reasonOtherWrap = this.form.querySelector('[data-wrf-other]');
    this.reasonOtherInput = this.form.querySelector('[data-wrf-field="reasonOther"]');
    this.reasonSelect.addEventListener('change', () => this.updateReasonOther_());

    this.form.addEventListener('submit', (event) => this.handleSubmit_(event));

    this.dropzone.addEventListener('click', () => this.fileInput.click());
    this.dropzone.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.fileInput.click();
      }
    });
    this.dropzone.addEventListener('dragover', (event) => {
      event.preventDefault();
      this.dropzone.classList.add('is-dragover');
    });
    this.dropzone.addEventListener('dragleave', () => {
      this.dropzone.classList.remove('is-dragover');
    });
    this.dropzone.addEventListener('drop', (event) => {
      event.preventDefault();
      this.dropzone.classList.remove('is-dragover');
      this.addFiles_(event.dataTransfer.files);
    });
    this.fileInput.addEventListener('change', () => {
      this.addFiles_(this.fileInput.files);
      this.fileInput.value = '';
    });
  }

  clearFieldError_(fieldName) {
    const field = this.form.querySelector(`[data-wrf-field="${fieldName}"]`);
    const errorEl = this.form.querySelector(`[data-wrf-error-for="${fieldName}"]`);
    if (field) field.closest('.wrf__field')?.classList.remove('has-error');
    if (errorEl) errorEl.textContent = '';
  }

  setFieldError_(fieldName, message) {
    const field = this.form.querySelector(`[data-wrf-field="${fieldName}"]`);
    const errorEl = this.form.querySelector(`[data-wrf-error-for="${fieldName}"]`);
    if (field) field.closest('.wrf__field')?.classList.add('has-error');
    if (errorEl) errorEl.textContent = message;
  }

  /** Shows the free-text "Other" box only when the "Other" reason is selected. */
  updateReasonOther_() {
    const isOther = this.reasonSelect.value === '__other__';
    this.reasonOtherWrap.hidden = !isOther;
    if (!isOther) {
      this.reasonOtherInput.value = '';
      this.clearFieldError_('reasonOther');
    }
  }

  /** The reason to send: the selected option's text, or the typed text when "Other". */
  reasonValue_() {
    if (this.reasonSelect.value === '__other__') {
      return this.reasonOtherInput.value.trim();
    }
    return this.reasonSelect.value;
  }

  /** Returns true if all required text fields are valid; sets inline errors otherwise. */
  validateFields_() {
    let isValid = true;
    const requiredFields = ['fullName', 'email', 'phone', 'orderNumber', 'dateStartedUsing', 'reasonForReturn'];

    requiredFields.forEach((fieldName) => {
      this.clearFieldError_(fieldName);
      const field = this.form.querySelector(`[data-wrf-field="${fieldName}"]`);
      const value = field ? field.value.trim() : '';

      if (!value) {
        this.setFieldError_(fieldName, this.strings.errorRequired);
        isValid = false;
        return;
      }

      if (fieldName === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        this.setFieldError_(fieldName, this.strings.errorEmail);
        isValid = false;
      }
    });

    // When "Other" is the selected reason, its free-text box is required too.
    this.clearFieldError_('reasonOther');
    if (this.reasonSelect.value === '__other__' && !this.reasonOtherInput.value.trim()) {
      this.setFieldError_('reasonOther', this.strings.errorRequired);
      isValid = false;
    }

    return isValid;
  }

  addFiles_(fileList) {
    Array.from(fileList).forEach((file) => {
      this.files.push({ file, previewUrl: URL.createObjectURL(file) });
    });
    this.renderPreviews_();
  }

  removeFile_(index) {
    const [removed] = this.files.splice(index, 1);
    if (removed) URL.revokeObjectURL(removed.previewUrl);
    this.renderPreviews_();
  }

  renderPreviews_() {
    this.previewList.innerHTML = '';
    this.files.forEach((entry, index) => {
      const item = document.createElement('li');
      item.className = 'wrf__preview';

      const img = document.createElement('img');
      img.src = entry.previewUrl;
      img.alt = entry.file.name;
      item.appendChild(img);

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'wrf__preview-remove';
      removeButton.setAttribute('aria-label', this.strings.photosRemove);
      removeButton.textContent = '×';
      removeButton.addEventListener('click', () => this.removeFile_(index));
      item.appendChild(removeButton);

      this.previewList.appendChild(item);
    });
  }

  /** Returns true if the accumulated files pass count/type checks; sets the images field error otherwise. */
  validateImages_() {
    this.clearFieldError_('images');

    if (this.files.length < 1) {
      this.setFieldError_('images', this.strings.errorMinImages);
      return false;
    }

    if (this.files.length > this.maxImages) {
      this.setFieldError_('images', this.strings.errorMaxImages.replace('{{ max }}', this.maxImages));
      return false;
    }

    for (const entry of this.files) {
      if (this.ALLOWED_MIME_TYPES.indexOf(entry.file.type) === -1) {
        this.setFieldError_('images', this.strings.errorFileType);
        return false;
      }
    }

    return true;
  }

  /**
   * Resizes to a max dimension and re-encodes as JPEG via Canvas, to shrink
   * the base64 payload before upload (raw phone photos are commonly 3-10MB;
   * this typically brings them under 1MB). Deliberately isolated as its own
   * method with a single (file) -> Promise<Blob> signature, so it can be
   * swapped for a different strategy later without touching its one caller.
   */
  compressImage_(file) {
    const maxDimension = 1600;
    const quality = 0.8;
    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDimension || height > maxDimension) {
          const scale = maxDimension / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(objectUrl);
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Image compression failed'));
            return;
          }
          resolve(blob);
        }, 'image/jpeg', quality);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load image for compression'));
      };
      img.src = objectUrl;
    });
  }

  handleSubmit_(event) {
    event.preventDefault();

    if (this.form.querySelector('[data-wrf-field="website"]').value) {
      // Honeypot tripped — silently pretend success without sending anything, so bots get no signal.
      this.showStatus_(this.strings.successMessage, 'success');
      return;
    }

    const fieldsValid = this.validateFields_();
    const imagesValid = this.validateImages_();
    if (!fieldsValid || !imagesValid) return;

    if (!this.scriptUrl) {
      this.showStatus_(this.strings.errorMessage, 'error');
      return;
    }

    this.setSubmitting_(true);
    this.encodeAndSend_().catch(() => {
      this.showStatus_(this.strings.errorGeneric, 'error');
      this.setSubmitting_(false);
    });
  }

  setSubmitting_(isSubmitting) {
    this.submitButton.disabled = isSubmitting;
    this.submitButton.textContent = isSubmitting ? this.strings.sending : this.defaultSubmitText;
  }

  showStatus_(message, kind) {
    this.statusEl.textContent = message;
    this.statusEl.classList.remove('is-success', 'is-error');
    this.statusEl.classList.add(kind === 'success' ? 'is-success' : 'is-error');
  }

  async encodeAndSend_() {
    const compressedBlobs = await Promise.all(this.files.map((entry) => this.compressImage_(entry.file)));

    const maxBytes = this.maxImageSizeMb * 1024 * 1024;
    if (compressedBlobs.some((blob) => blob.size > maxBytes)) {
      this.showStatus_(this.strings.errorMaxSize.replace('{{ max_mb }}', this.maxImageSizeMb), 'error');
      this.setSubmitting_(false);
      return;
    }

    const images = await Promise.all(
      compressedBlobs.map(
        (blob, index) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const base64 = reader.result.split(',')[1];
              const baseName = this.files[index].file.name.replace(/\.[^.]+$/, '');
              resolve({ filename: baseName + '.jpg', mimeType: 'image/jpeg', base64 });
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
          })
      )
    );

    const payload = {
      fullName: this.form.querySelector('[data-wrf-field="fullName"]').value.trim(),
      email: this.form.querySelector('[data-wrf-field="email"]').value.trim(),
      phone: this.form.querySelector('[data-wrf-field="phone"]').value.trim(),
      orderNumber: this.form.querySelector('[data-wrf-field="orderNumber"]').value.trim(),
      dateStartedUsing: this.form.querySelector('[data-wrf-field="dateStartedUsing"]').value,
      reasonForReturn: this.reasonValue_(),
      submittedAt: new Date().toISOString(),
      images
    };

    try {
      await this.sendRequest_(payload);
      this.showStatus_(this.strings.successMessage, 'success');
      this.resetForm_();
    } catch (err) {
      this.showStatus_(this.strings.errorMessage, 'error');
    }
    this.setSubmitting_(false);
  }

  /**
   * Google Apps Script Web App responses cannot carry CORS headers (a fixed
   * Apps Script platform limitation — confirmed against a real deployment,
   * not something fixable in this script or that script). `mode: 'no-cors'`
   * still lets doPost run fully server-side (Sheet row, Drive upload, both
   * emails all happen normally) but makes the response opaque: this promise
   * only reports whether the request left the browser, never whether the
   * script itself succeeded — there is no server-confirmed success/failure
   * signal available here, by design of this fallback.
   */
  sendRequest_(payload) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);
    return fetch(this.scriptUrl, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload),
      signal: controller.signal
    }).finally(() => clearTimeout(timeoutId));
  }

  resetForm_() {
    this.form.reset();
    this.updateReasonOther_();
    this.files.forEach((entry) => URL.revokeObjectURL(entry.previewUrl));
    this.files = [];
    this.renderPreviews_();
  }
}

customElements.define('wd-return-request-form', WdReturnRequestForm);
